import {
  SkyButton,
  SkyDataGrid,
  SkyDialog,
  SkyInput,
  SkyMessageBar,
  SkyRadioGroup,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import {
  MAX_CONNECTOR_VERSIONS,
  type ConnectorAdminVersionView,
  type ConnectorAdminView,
} from "@shared/types/connectorsAdmin";
import { EMPTY_CELL } from "@/components/platform/format";
import { ErrorState } from "@/components/platform/ScreenState";
import { AddIcon, DeleteIcon } from "@/lib/icons";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * Image tags for one connector definition (FR-32, canvas 13 panel 03).
 *
 * Two rules, both enforced on the server as well as here, which is the point:
 * a definition carries at most five tags, and exactly one of them is the
 * default. The UI refuses early so the reader learns the rule before losing
 * their typing to a 409; the server refuses regardless, so a stale tab cannot
 * slip a sixth tag past it.
 *
 * The default is chosen from ONE radio group, so arrow keys move it and a
 * screen reader announces "1 of 5"; a column of buttons that happened to look
 * like radios would announce five unrelated controls and move focus with Tab
 * instead. The group sits beside the grid rather than inside a Default column,
 * because a cell can only hold one MEMBER of a group and `SkyRadioGroup` owns
 * the labelling of its members — there is no way to put a named, grouped radio
 * in a cell without dropping to a raw `<input type="radio">`, which paints the
 * browser's own blue on a dark dialog and carries no semantic token. The column
 * that remains says which tag is the default; the group is what changes it.
 */

export function ConnectorVersionsDialog({
  open,
  onOpenChange,
  connector,
  onAddVersion,
  onRemoveVersion,
  onSetDefault,
  isSaving,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connector: ConnectorAdminView | null;
  onAddVersion: (values: { tag: string; label?: string; notes?: string }) => void;
  onRemoveVersion: (tag: string) => void;
  onSetDefault: (tag: string) => void;
  isSaving: boolean;
  error: unknown;
}) {
  const zone = useDisplayTz();
  // The caller remounts this component when the connector changes, so the
  // add-tag fields start empty for each definition without an effect that would
  // also clear them on every list refetch.
  const [tag, setTag] = useState("");
  const [notes, setNotes] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  if (!connector) return null;

  const versions = connector.versions;
  const atCap = versions.length >= MAX_CONNECTOR_VERSIONS;
  const defaultTag = versions.find((version) => version.isDefault)?.tag ?? "";

  function addTag() {
    const trimmed = tag.trim();
    if (trimmed === "") {
      setLocalError("Enter the image tag to add, for example v1.7.0.");
      return;
    }
    if (versions.some((version) => version.tag === trimmed)) {
      setLocalError(`${connector!.name} already carries ${trimmed}. Pick another tag.`);
      return;
    }
    if (atCap) {
      setLocalError(
        `A definition carries at most ${MAX_CONNECTOR_VERSIONS} tags. Remove one before adding another.`,
      );
      return;
    }
    setLocalError(null);
    onAddVersion({ tag: trimmed, ...(notes.trim() === "" ? {} : { notes: notes.trim() }) });
    setTag("");
    setNotes("");
  }

  function removeTag(version: ConnectorAdminVersionView) {
    if (version.isDefault) {
      // Refused with the reason rather than greyed out silently: a disabled
      // control teaches nothing, and the reason IS the next action.
      setLocalError(
        `${version.tag} is the default. Make another tag the default first, then remove it.`,
      );
      return;
    }
    setLocalError(null);
    onRemoveVersion(version.tag);
  }

  const columns: SkyDataGridColumn<ConnectorAdminVersionView>[] = [
    {
      key: "isDefault",
      header: "Default",
      width: "12%",
      align: "center",
      renderCell: (_value, row) => (
        <SkyText size={200} color={row.isDefault ? "accent" : "muted"}>
          {row.isDefault ? "Default" : EMPTY_CELL}
        </SkyText>
      ),
    },
    {
      key: "tag",
      header: "Tag",
      width: "22%",
      renderCell: (_value, row) => (
        <div className="flex flex-col">
          <SkyText size={300} font="monospace" block>
            {row.tag}
          </SkyText>
          {row.label ? (
            <SkyText size={200} color="muted" block>
              {row.label}
            </SkyText>
          ) : null}
        </div>
      ),
    },
    {
      key: "releasedAt",
      header: "Released",
      width: "18%",
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric">
          {formatInstant(row.releasedAt, zone, "day")}
        </SkyText>
      ),
    },
    {
      key: "notes",
      header: "Notes",
      width: "34%",
      renderCell: (_value, row) => <SkyText size={200}>{row.notes ?? EMPTY_CELL}</SkyText>,
    },
    {
      key: "actions",
      header: "Actions",
      width: "14%",
      renderCell: (_value, row) => (
        <SkyButton
          appearance="subtle"
          size="small"
          icon={DeleteIcon}
          disabled={isSaving}
          onClick={() => removeTag(row)}
          aria-label={`Remove ${row.tag} from ${connector.name}`}
        >
          Remove
        </SkyButton>
      ),
    },
  ];

  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Image tags for ${connector.name}`}
      description={`A definition carries up to ${MAX_CONNECTOR_VERSIONS} tags and exactly one default. The default is what a run uses unless an instance pins another.`}
      size="large"
      testId="connector-versions-dialog"
      secondaryAction={{ label: "Close", onClick: () => onOpenChange(false) }}
    >
      <div className="flex flex-col gap-4">
        {error ? <ErrorState title="That change was not saved" error={error} /> : null}
        {localError ? (
          <SkyMessageBar
            intent="error"
            layout="multiline"
            title="That tag was not added"
            message={localError}
            testId="version-local-error"
          />
        ) : null}

        <SkyDataGrid<ConnectorAdminVersionView>
          data={versions}
          columns={columns}
          getRowId={(row) => row.tag}
          emptyMessage="This definition carries no image tags yet."
          testId="connector-versions-grid"
        />

        {versions.length > 0 ? (
          <SkyRadioGroup
            label="Default tag"
            testId="default-tag-group"
            layout="vertical"
            name={`default-tag-${connector.name}`}
            value={defaultTag}
            disabled={isSaving}
            options={versions.map((version) => ({
              value: version.tag,
              label: version.tag,
              ...(version.label ? { description: version.label } : {}),
            }))}
            onChange={(next) => {
              setLocalError(null);
              onSetDefault(next);
            }}
          />
        ) : null}

        {atCap ? (
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            title={`${MAX_CONNECTOR_VERSIONS} tags is the cap`}
            testId="version-cap"
            message="Remove a tag before adding another. Removing the default is refused. Make another tag the default first."
          />
        ) : null}

        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56">
            <SkyInput
              label="New tag"
              value={tag}
              disabled={atCap}
              placeholder="v1.7.0"
              onChange={(event) => setTag(event.target.value)}
              testId="new-version-tag"
            />
          </div>
          <div className="min-w-64 flex-1">
            <SkyInput
              label="Notes"
              value={notes}
              disabled={atCap}
              onChange={(event) => setNotes(event.target.value)}
              testId="new-version-notes"
            />
          </div>
          <SkyButton
            appearance="secondary"
            icon={AddIcon}
            disabled={atCap}
            loading={isSaving}
            onClick={addTag}
            testId="add-version"
          >
            Add tag
          </SkyButton>
        </div>

        <SkyText size={200} color="muted" block>
          {versions.length} of {MAX_CONNECTOR_VERSIONS} tags.{" "}
          {defaultTag === "" ? "No tag is the default yet." : `${defaultTag} is the default.`}
        </SkyText>
      </div>
    </SkyDialog>
  );
}
